import * as XLSX from 'xlsx-js-style';
import EnumsService from '../../Services/enums.service';
import { buildReportSheet, applyWorkbookRtl } from '../../Utils/ExcelReport';
import { roundVatToInteger } from '../../Utils/FormatNumber';
import { formatAmount } from './SubComponents/SectionParts';
import { VAT_ROWS } from './SubComponents/VatSection';
import {
    FOLLOW_UP_SECTIONS,
    FOLLOW_UP_STATUS_OPTIONS,
    MANUAL_KEYS,
    PAYMENT_METHOD_OPTIONS,
    formatPeriodLabel,
    getMonthName,
    getSectionReportingType,
    isBlankValue as isBlank,
    reportDiffers,
    reportingTypeLabel,
    toNumber,
} from './reportingFollowUpConstants';

// Row cells are numbers (amounts), strings (counts, percentages, text) or null (no value).
// `mismatch` is aligned with `values` and marks reported cells that differ from the books.

const manualNumber = (value) => (isBlank(value) ? null : toNumber(value));

const count = (value) => (value === null || value === undefined || value === '' ? null : String(value));

const formatDate = (dateString) => {
    const match = String(dateString || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

const optionLabel = (t, options, value) => t((options.find((o) => o.value === value) || options[0]).labelKey);

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const safeFileName = (value) => String(value || '').replace(/[\\/:*?"<>|]+/g, ' ').trim();

const personNames = ({ t, client, followUp }) => ({
    ownerName: followUp?.businessOwnerName || client?.businessOwnerName || client?.name || t('businessOwnerName'),
    spouseName: followUp?.spouseName || client?.spouseName || '',
});

/**
 * Every amount the client may owe for the report month, using the Report column once it is
 * filled in and the books otherwise. Zero amounts are kept; consumers drop them.
 */
export const collectFollowUpPayments = ({ t, client, state, followUp, partnerName }) => {
    const payments = [];
    const { vat, advance } = state;

    if (vat) {
        payments.push({
            key: 'vat',
            label: t('FollowUpPaymentVat'),
            refundLabel: t('FollowUpPaymentVatRefund'),
            amount: vat.hasReport ? vat.reportToPay : vat.books.toPay,
        });
    }

    if (advance) {
        payments.push({
            key: 'advance',
            label: partnerName ? `${t('FollowUpPaymentAdvances')} - ${partnerName}` : t('FollowUpPaymentAdvances'),
            refundLabel: partnerName
                ? `${t('FollowUpPaymentAdvancesCredit')} - ${partnerName}`
                : t('FollowUpPaymentAdvancesCredit'),
            amount: advance.hasReport ? advance.reportComputed.toPay : advance.books.toPay,
        });
    }

    const wKeys = MANUAL_KEYS.WITHHOLDING;
    const withholding = state.getTracking(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING).manualValues || {};
    const withholdingReported = !isBlank(withholding[wKeys.reportTotalWithholding])
        || !isBlank(withholding[wKeys.reportSalaryWithholding]);
    payments.push({
        key: 'withholding',
        label: t('FollowUpPaymentWithholding'),
        amount: withholdingReported
            ? toNumber(withholding[wKeys.reportTotalWithholding]) + toNumber(withholding[wKeys.reportSalaryWithholding])
            : (followUp?.withholdingBooks?.totalWithholding ?? 0),
    });

    const niKeys = MANUAL_KEYS.PERSONAL_NI;
    const ni = state.getTracking(FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE).manualValues || {};
    const { ownerName, spouseName } = personNames({ t, client, followUp });
    payments.push({
        key: 'personalNiOwner',
        label: `${t('FollowUpPaymentPersonalNi')} - ${ownerName}`,
        amount: toNumber(ni[niKeys.ownerAssessmentPayment]) + toNumber(ni[niKeys.ownerCurrentPayment]),
    });
    if (spouseName) {
        payments.push({
            key: 'personalNiSpouse',
            label: `${t('FollowUpPaymentPersonalNi')} - ${spouseName}`,
            amount: toNumber(ni[niKeys.spouseAssessmentPayment]) + toNumber(ni[niKeys.spouseCurrentPayment]),
        });
    }

    const niw = state.getTracking(FOLLOW_UP_SECTIONS.NATIONAL_INSURANCE_WITHHOLDING).manualValues || {};
    payments.push({
        key: 'niWithholding',
        label: t('FollowUpPaymentNiWithholding'),
        amount: toNumber(niw[MANUAL_KEYS.NI_WITHHOLDING.amountToPay]),
    });

    payments.push({
        key: 'outstandingFee',
        label: t('FollowUpOutstandingFeeBalance'),
        amount: toNumber(state.outstandingFeeBalance),
    });

    return payments.map((p) => ({ ...p, amount: roundVatToInteger(p.amount || 0) }));
};

/**
 * Snapshot of what the follow-up modal currently shows (including unsaved edits), shared by the
 * PDF, print and Excel exports.
 */
export const buildFollowUpReport = ({ t, language, client, year, month, state, followUp, partnerName }) => {
    const { periods } = state;

    const trackingRows = (tracking) => [
        { label: t('FollowUpReportDate'), value: formatDate(tracking.reportDate) },
        { label: t('FollowUpPaymentDate'), value: formatDate(tracking.paymentDate) },
        { label: t('FollowUpPaymentMethod'), value: optionLabel(t, PAYMENT_METHOD_OPTIONS, tracking.paymentMethod) },
        { label: t('FollowUpStatus'), value: optionLabel(t, FOLLOW_UP_STATUS_OPTIONS, tracking.followUpStatus) },
        { label: t('FollowUpReportNote'), value: tracking.note || '', wide: true },
    ];

    const sectionReportingType = (section) => reportingTypeLabel(getSectionReportingType(client, section), t);

    const sections = [];

    const { vat } = state;
    if (vat) {
        sections.push({
            key: 'vat',
            title: t('FollowUpVatTitle'),
            fileNumber: followUp?.vatFileNumber,
            reportingType: sectionReportingType(FOLLOW_UP_SECTIONS.VAT),
            periodLabel: formatPeriodLabel(periods.vat, language),
            isReported: vat.isReported,
            columns: [t('FollowUpBooks'), t('FollowUpReport')],
            rows: [
                ...VAT_ROWS.map(({ field, labelKey }) => ({
                    label: t(labelKey),
                    values: [vat.books[field], manualNumber(vat.report[field])],
                    mismatch: [false, vat.mismatch[field]],
                })),
                {
                    label: t('FollowUpToPayOrRefund'),
                    values: [vat.books.toPay, vat.hasReport ? vat.reportToPay : null],
                    mismatch: [false, vat.mismatch.toPay],
                    total: true,
                },
                { label: t('FollowUpBusinessesInReport'), values: [count(vat.businessesCount), null] },
            ],
            members: vat.members,
            tracking: trackingRows(state.getTracking(FOLLOW_UP_SECTIONS.VAT)),
        });
    }

    const { advance } = state;
    const advancePartnerId = client?.isPartners ? state.selectedPartnerId : null;
    const advanceTracking = state.getTracking(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, advancePartnerId);
    const advanceManual = advanceTracking.manualValues || {};
    const aKeys = MANUAL_KEYS.ADVANCE;
    const usePercentage = advance?.settings?.usePercentage ?? true;
    const am = advance?.mismatch || {};
    const reportedOrNull = (value) => (advance?.hasReport ? value : null);
    const advanceRows = advance ? [
        {
            label: t('FollowUpBusinessTurnover'),
            values: [advance.books.turnover, manualNumber(advance.report.witholdingIncomesNoVat)],
            mismatch: [false, am.turnover],
        },
        {
            label: t('FollowUpAdvancePercentage'),
            values: usePercentage
                ? [
                    `${advance.books.percentage}%`,
                    isBlank(advance.report.incomeTaxAdvancePercentage) ? null : `${advance.report.incomeTaxAdvancePercentage}%`,
                ]
                : [null, null],
            mismatch: [false, am.percentage],
        },
        {
            label: t('FollowUpAdvanceAmount'),
            values: [
                advance.books.advanceAmount,
                usePercentage
                    ? reportedOrNull(advance.reportComputed.advanceAmount)
                    : manualNumber(advance.report.incomeTaxAdvanceAmount),
            ],
            mismatch: [false, am.advanceAmount],
        },
        {
            label: t('FollowUpWithholdingInPeriod'),
            values: [advance.books.withholding, manualNumber(advance.report.acceptedWithholdingTax)],
            mismatch: [false, am.withholding],
        },
        {
            label: t('FollowUpWithholdingForOffset'),
            values: [advance.books.withholdingOffset, reportedOrNull(advance.reportComputed.withholdingOffset)],
            mismatch: [false, am.withholdingOffset],
        },
        {
            label: t('FollowUpAdvanceToPay'),
            values: [advance.books.toPay, reportedOrNull(advance.reportComputed.toPay)],
            mismatch: [false, am.toPay],
            total: true,
        },
        { label: t('FollowUpBusinessesInReport'), values: [count(advance.businessesCount), null] },
    ] : [];
    sections.push({
        key: 'advance',
        title: partnerName ? `${t('FollowUpAdvanceTitle')} - ${partnerName}` : t('FollowUpAdvanceTitle'),
        fileNumber: followUp?.taxFileNumber,
        reportingType: sectionReportingType(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE),
        periodLabel: formatPeriodLabel(periods.advance, language),
        isReported: advance ? advance.isReported : undefined,
        columns: [t('FollowUpBooks'), t('FollowUpReport')],
        rows: [
            ...advanceRows,
            {
                label: t('FollowUpPriorExcessDeductions'),
                values: [
                    advance?.books.priorExcessDeductions ?? null,
                    advance?.reportComputed.priorExcessDeductions ?? null,
                ],
                mismatch: [false, am.priorExcessDeductions],
            },
            {
                label: t('FollowUpPeriodExcessDeductions'),
                values: [
                    advance ? advance.books.periodExcessDeductions : null,
                    reportedOrNull(advance?.reportComputed.periodExcessDeductions ?? null),
                ],
                mismatch: [false, am.periodExcessDeductions],
            },
            {
                label: t('FollowUpExcessDeductionsNextReport'),
                values: [
                    advance?.books.excessDeductions ?? null,
                    reportedOrNull(advance?.reportComputed.excessDeductions ?? null),
                ],
                mismatch: [false, am.excessDeductions],
                total: true,
            },
            {
                label: t('FollowUpExcessPaymentsNextReport'),
                values: [manualNumber(advanceManual[aKeys.excessPaymentsBooks]), manualNumber(advanceManual[aKeys.excessPaymentsReport])],
            },
        ],
        members: advance?.members,
        tracking: trackingRows(advanceTracking),
    });

    const withholdingTracking = state.getTracking(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING);
    const wm = withholdingTracking.manualValues || {};
    const wKeys = MANUAL_KEYS.WITHHOLDING;
    const books = followUp?.withholdingBooks ?? null;
    const withholdingRow = (labelKey, booksValue, reportValue, asCount = false) => ({
        label: t(labelKey),
        values: asCount ? [count(booksValue), count(reportValue)] : [booksValue ?? null, manualNumber(reportValue)],
        mismatch: [false, reportDiffers(booksValue ?? null, reportValue)],
    });
    sections.push({
        key: 'withholding',
        title: t('FollowUpWithholdingTitle'),
        fileNumber: followUp?.deductionsFileNumber,
        reportingType: sectionReportingType(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING),
        periodLabel: formatPeriodLabel(periods.withholding, language),
        isReported: !!state.withholdingReported,
        columns: [t('FollowUpBooks'), t('FollowUpReport')],
        rows: [
            withholdingRow('FollowUpNumberOfRecipients', books?.numberOfRecipients, wm[wKeys.reportRecipients], true),
            withholdingRow('FollowUpTotalPayments', books?.totalPayments, wm[wKeys.reportTotalPayments]),
            withholdingRow('FollowUpTotalWithholding', books?.totalWithholding, wm[wKeys.reportTotalWithholding]),
            { label: t('FollowUpTotalSalaries'), values: [null, manualNumber(wm[wKeys.reportTotalSalaries])] },
            { label: t('FollowUpSalaryWithholding'), values: [null, manualNumber(wm[wKeys.reportSalaryWithholding])] },
            {
                label: t('FollowUpTotalWithholdingToPay'),
                values: [
                    books?.totalWithholding ?? null,
                    toNumber(wm[wKeys.reportTotalWithholding]) + toNumber(wm[wKeys.reportSalaryWithholding]),
                ],
                total: true,
            },
        ],
        tracking: trackingRows(withholdingTracking),
    });

    const niTracking = state.getTracking(FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE);
    const ni = niTracking.manualValues || {};
    const niKeys = MANUAL_KEYS.PERSONAL_NI;
    const { ownerName, spouseName } = personNames({ t, client, followUp });
    const niRow = (labelKey, ownerKey, spouseKey) => ({
        label: t(labelKey),
        values: [manualNumber(ni[ownerKey]), ...(spouseName ? [manualNumber(ni[spouseKey])] : [])],
    });
    sections.push({
        key: 'personalNi',
        title: t('FollowUpPersonalNiTitle'),
        fileNumber: followUp?.taxFileNumber,
        periodLabel: formatPeriodLabel(periods.nationalInsurance, language),
        columns: spouseName ? [ownerName, spouseName] : [ownerName],
        rows: [
            niRow('FollowUpAssessmentDifferencesPayment', niKeys.ownerAssessmentPayment, niKeys.spouseAssessmentPayment),
            niRow('FollowUpCurrentPayment', niKeys.ownerCurrentPayment, niKeys.spouseCurrentPayment),
            niRow('FollowUpInsuredIncome', niKeys.ownerInsuredIncome, niKeys.spouseInsuredIncome),
        ],
        tracking: trackingRows(niTracking),
    });

    const niwTracking = state.getTracking(FOLLOW_UP_SECTIONS.NATIONAL_INSURANCE_WITHHOLDING);
    const niw = niwTracking.manualValues || {};
    const niwKeys = MANUAL_KEYS.NI_WITHHOLDING;
    sections.push({
        key: 'niWithholding',
        title: t('FollowUpNiWithholdingTitle'),
        fileNumber: followUp?.deductionsFileNumber,
        periodLabel: formatPeriodLabel(periods.nationalInsurance, language),
        columns: [t('FollowUpReport')],
        rows: [
            { label: t('FollowUpNumberOfEmployees'), values: [count(niw[niwKeys.numberOfEmployees])] },
            { label: t('FollowUpNiWithholdingToPay'), values: [manualNumber(niw[niwKeys.amountToPay])], total: true },
        ],
        tracking: trackingRows(niwTracking),
    });

    const payments = collectFollowUpPayments({ t, client, state, followUp, partnerName })
        .filter((p) => p.amount !== 0)
        .map((p) => ({ label: p.amount < 0 && p.refundLabel ? p.refundLabel : p.label, amount: p.amount }));

    return {
        title: t('ReportingFollowUp'),
        clientName: client?.name || '',
        caseId: client?.caseId || '',
        monthLabel: `${String(month).padStart(2, '0')} - ${getMonthName(month, language)} ${year}`,
        year,
        month,
        materialReceivedDate: formatDate(state.materialReceivedDate),
        outstandingFeeBalance: isBlank(state.outstandingFeeBalance) ? null : toNumber(state.outstandingFeeBalance),
        sections,
        payments,
        paymentsTotal: payments.filter((p) => p.amount > 0).reduce((sum, p) => sum + p.amount, 0),
    };
};

const fileBaseName = (report) =>
    safeFileName(`${report.clientName}-${report.caseId}_${report.title}_${String(report.month).padStart(2, '0')}-${report.year}`);

// ------------------------------------------------------------------------------------ HTML

const formatCell = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return typeof value === 'number' ? formatAmount(value) : escapeHtml(value);
};

const sectionHtml = (section, t) => {
    const status = section.isReported === undefined
        ? ''
        : `<span class="status ${section.isReported ? 'status-reported' : 'status-unreported'}">
               ${escapeHtml(section.isReported ? t('FollowUpReported') : t('FollowUpNotReported'))}
           </span>`;

    const members = section.members?.length > 1
        ? `<div class="members"><b>${escapeHtml(t('FollowUpReportedTogether'))}:</b>
               ${section.members.map((m) => escapeHtml(m.name ? `${m.name} · ${m.caseId}` : m.caseId)).join(', ')}
           </div>`
        : '';

    return `
        <div class="card">
            <div class="card-head">
                <div class="card-title">${escapeHtml(section.title)}</div>
                <div class="card-meta">
                    ${section.reportingType ? `<span class="file">${escapeHtml(section.reportingType)}</span>` : ''}
                    ${section.fileNumber ? `<span class="file">${escapeHtml(t('FollowUpFileNumber'))}: ${escapeHtml(section.fileNumber)}</span>` : ''}
                    ${status}
                </div>
            </div>
            <table class="values">
                <thead>
                    <tr>
                        <th class="label-col period">${escapeHtml(section.periodLabel)}</th>
                        ${section.columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}
                    </tr>
                </thead>
                <tbody>
                    ${section.rows.map((row) => `
                        <tr class="${row.total ? 'total' : ''}">
                            <td class="label-col">${escapeHtml(row.label)}</td>
                            ${row.values.map((v, i) => `<td class="num${row.mismatch?.[i] ? ' diff' : ''}">${formatCell(v)}</td>`).join('')}
                        </tr>`).join('')}
                </tbody>
            </table>
            ${members}
            <table class="tracking">
                <tbody>
                    ${section.tracking.map((row) => `
                        <tr>
                            <td class="label-col">${escapeHtml(row.label)}</td>
                            <td>${escapeHtml(row.value) || '—'}</td>
                        </tr>`).join('')}
                </tbody>
            </table>
        </div>`;
};

const paymentsHtml = (report, t) => `
    <div class="card payments">
        <div class="card-head">
            <div class="card-title">${escapeHtml(t('FollowUpPaymentsSummary'))}</div>
        </div>
        <table class="values">
            <tbody>
                ${report.payments.length === 0
        ? `<tr><td class="label-col" colspan="2">${escapeHtml(t('FollowUpNoPaymentsDue'))}</td></tr>`
        : report.payments.map((p) => `
                    <tr class="${p.amount < 0 ? 'refund' : ''}">
                        <td class="label-col">${escapeHtml(p.label)}</td>
                        <td class="num">${formatAmount(Math.abs(p.amount))}</td>
                    </tr>`).join('')}
                <tr class="total grand">
                    <td class="label-col">${escapeHtml(t('FollowUpTotalToPay'))}</td>
                    <td class="num">${formatAmount(report.paymentsTotal)}</td>
                </tr>
            </tbody>
        </table>
    </div>`;

/** A4 landscape, one page: header band, then a 3-column grid of the five sections and the payments summary. */
export const buildFollowUpHtml = (report, t) => {
    const now = new Date();
    const printed = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`
        + ` ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const metaItem = (label, value) => `
        <div class="meta-item"><span>${escapeHtml(label)}</span><b>${escapeHtml(value)}</b></div>`;

    return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(report.title)}</title>
<style>
    @page { size: A4 landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
        direction: rtl;
        font-family: "Heebo", Arial, sans-serif;
        font-size: 10px;
        color: #141e2b;
        background: #ffffff;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .report-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 8px 14px;
        margin-bottom: 8px;
        border-radius: 10px;
        background: linear-gradient(135deg, #304fff 0%, #4a6cf7 100%);
        color: #ffffff;
    }
    .report-title { margin: 0; font-size: 17px; font-weight: 700; }
    .report-client { margin-top: 2px; font-size: 11px; opacity: 0.9; }
    .meta { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
    .meta-item {
        display: flex; flex-direction: column; gap: 1px;
        padding: 3px 10px; border-radius: 8px; background: rgba(255, 255, 255, 0.16);
        font-size: 8.5px; min-width: 80px;
    }
    .meta-item b { font-size: 10.5px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; align-items: start; }
    .card { border: 1px solid #e5e7f6; border-radius: 8px; overflow: hidden; break-inside: avoid; page-break-inside: avoid; }
    .card-head {
        display: flex; align-items: center; justify-content: space-between; gap: 6px;
        padding: 5px 8px; background: #f3f6ff; border-bottom: 1px solid #e5e7f6;
    }
    .card-title { font-size: 11px; font-weight: 700; }
    .card-meta { display: flex; align-items: center; gap: 6px; }
    .file { font-size: 9px; font-weight: 600; color: #304fff; }
    .status { padding: 1px 8px; border-radius: 999px; font-size: 8.5px; font-weight: 600; }
    .status-reported { color: #1f9d55; background: #e6f5ec; }
    .status-unreported { color: #c62828; background: #fbeaea; }
    table.values { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.values th {
        padding: 3px 5px; background: #eef2ff; color: #304fff; font-weight: 700;
        border-bottom: 1px solid #e5e7f6; text-align: center;
    }
    table.values td { padding: 3px 5px; border-bottom: 1px solid #f0f1f8; }
    .label-col { width: 46%; text-align: right; }
    th.period { color: #141e2b; text-align: right; }
    td.num { text-align: left; direction: ltr; font-variant-numeric: tabular-nums; }
    td.diff { color: #c62828; font-weight: 700; box-shadow: inset 0 0 0 1.5px #e53935; background: #fff5f5; }
    tr.total td { font-weight: 700; background: #f3f6ff; border-top: 1.5px solid #304fff; }
    tr.refund td { color: #1f9d55; }
    tr.grand td { font-size: 11px; color: #304fff; }
    .members { padding: 3px 8px; font-size: 8.5px; background: #f8f9ff; border-bottom: 1px solid #f0f1f8; }
    table.tracking { width: 100%; border-collapse: collapse; table-layout: fixed; border-top: 1px solid #e5e7f6; }
    table.tracking td { padding: 2.5px 5px; border-bottom: 1px solid #f0f1f8; font-weight: 600; overflow-wrap: anywhere; }
    table.tracking td.label-col { color: rgba(20, 30, 43, 0.6); font-weight: 400; }
    table.tracking tr:last-child td { border-bottom: none; }
    .legend { margin-top: 6px; font-size: 8.5px; color: rgba(20, 30, 43, 0.6); }
    .legend .swatch {
        display: inline-block; width: 10px; height: 8px; margin-inline-end: 4px; vertical-align: middle;
        border: 1.5px solid #e53935; background: #fff5f5; border-radius: 2px;
    }
</style>
</head>
<body>
    <div class="report-header">
        <div>
            <h1 class="report-title">${escapeHtml(report.title)}</h1>
            <div class="report-client">${escapeHtml(report.clientName)} · ${escapeHtml(report.caseId)}</div>
        </div>
        <div class="meta">
            ${metaItem(t('FollowUpReportMonth'), report.monthLabel)}
            ${report.materialReceivedDate ? metaItem(t('FollowUpMaterialReceivedDate'), report.materialReceivedDate) : ''}
            ${report.outstandingFeeBalance != null ? metaItem(t('FollowUpOutstandingFeeBalance'), `${formatAmount(report.outstandingFeeBalance)} ₪`) : ''}
            ${metaItem(t('FollowUpPrintedAt'), printed)}
        </div>
    </div>
    <div class="grid">
        ${report.sections.map((s) => sectionHtml(s, t)).join('')}
        ${paymentsHtml(report, t)}
    </div>
    <div class="legend"><span class="swatch"></span>${escapeHtml(t('FollowUpReportDiffersFromBooks'))}</div>
</body>
</html>`;
};

// ------------------------------------------------------------------------------------ PDF / print

export const downloadFollowUpPdf = async (report, t, token) => {
    const html = buildFollowUpHtml(report, t);
    const response = await EnumsService.GeneratePdfFromHtml(html, report.caseId, token);
    if (!response?.ok) throw new Error(`PDF generation failed: ${response?.status}`);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileBaseName(report)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const printFollowUp = (report, t) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(buildFollowUpHtml(report, t));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
};

// ------------------------------------------------------------------------------------ Excel

const SHEET_NAME_INVALID = /[\\/?*[\]:]/g;

const MISMATCH_FONT = { bold: true, color: { rgb: 'FFC62828' } };
const MISMATCH_BORDER = { style: 'medium', color: { rgb: 'FFE53935' } };

const appendSheet = (wb, usedNames, title, ws) => {
    let name = title.replace(SHEET_NAME_INVALID, ' ').slice(0, 31).trim() || 'Sheet';
    for (let n = 2; usedNames.has(name); n += 1) name = `${name.slice(0, 28)} ${n}`;
    usedNames.add(name);
    XLSX.utils.book_append_sheet(wb, ws, name);
};

// Amounts are shown rounded to whole shekels, like the modal.
const formatWholeShekels = (ws) => {
    Object.keys(ws).forEach((addr) => {
        if (!addr.startsWith('!') && ws[addr]?.t === 'n') ws[addr].z = '#,##0;(#,##0)';
    });
};

export const downloadFollowUpExcel = (report, t) => {
    const wb = XLSX.utils.book_new();
    const usedNames = new Set();
    const contextLine = (fileNumber, reportingType) => [
        fileNumber ? `${t('FollowUpFileNumber')}: ${fileNumber}` : null,
        reportingType ? `${t('ReportingType')}: ${reportingType}` : null,
        `${t('FollowUpReportMonth')}: ${report.monthLabel}`,
        report.materialReceivedDate ? `${t('FollowUpMaterialReceivedDate')}: ${report.materialReceivedDate}` : null,
    ].filter(Boolean).join(' · ');

    report.sections.forEach((section) => {
        const header = [section.periodLabel || '', ...section.columns];
        const width = header.length;
        const pad = (cells) => [...cells, ...Array(Math.max(width - cells.length, 0)).fill('')];

        const valueRows = section.rows.map((row) => [
            row.label,
            ...row.values.map((v) => (typeof v === 'number' ? roundVatToInteger(v) : (v ?? ''))),
        ]);
        const trackingRows = section.tracking.map((row) => pad([row.label, row.value || '']));
        const memberRows = section.members?.length > 1
            ? [pad([t('FollowUpReportedTogether'), section.members.map((m) => (m.name ? `${m.name} · ${m.caseId}` : m.caseId)).join(', ')])]
            : [];
        const statusRow = section.isReported === undefined
            ? []
            : [pad([section.isReported ? t('FollowUpReported') : t('FollowUpNotReported')])];

        const rows = [...valueRows, pad([]), ...memberRows, ...statusRow, ...trackingRows];
        const totalRowIndexes = section.rows.map((row, i) => (row.total ? i : -1)).filter((i) => i >= 0);
        const titleLines = [`${report.clientName} · ${report.caseId}`, section.title, contextLine(section.fileNumber, section.reportingType)];

        const ws = buildReportSheet({
            titleLines,
            header,
            rows,
            colWidths: [34, ...section.columns.map(() => 18)],
            numericColIndexes: section.columns.map((_, i) => i + 1),
            totalRowIndexes,
        });
        formatWholeShekels(ws);

        const firstDataRow = titleLines.length + 2;
        section.rows.forEach((row, r) => {
            (row.mismatch || []).forEach((isDiff, c) => {
                const cell = ws[XLSX.utils.encode_cell({ r: firstDataRow + r, c: c + 1 })];
                if (!isDiff || !cell?.s) return;
                cell.s = {
                    ...cell.s,
                    font: { ...cell.s.font, ...MISMATCH_FONT },
                    border: { top: MISMATCH_BORDER, bottom: MISMATCH_BORDER, left: MISMATCH_BORDER, right: MISMATCH_BORDER },
                };
            });
        });

        appendSheet(wb, usedNames, section.title, ws);
    });

    const paymentRows = report.payments.map((p) => [p.label, Math.abs(p.amount)]);
    const paymentsWs = buildReportSheet({
        titleLines: [`${report.clientName} · ${report.caseId}`, t('FollowUpPaymentsSummary'), contextLine(null)],
        header: [t('FollowUpPaymentFor'), t('FollowUpAmount')],
        rows: [...paymentRows, [t('FollowUpTotalToPay'), report.paymentsTotal]],
        colWidths: [40, 18],
        numericColIndexes: [1],
        totalRowIndexes: [paymentRows.length],
    });
    formatWholeShekels(paymentsWs);
    appendSheet(wb, usedNames, t('FollowUpPaymentsSummary'), paymentsWs);

    applyWorkbookRtl(wb);
    XLSX.writeFile(wb, `${fileBaseName(report)}.xlsx`);
};
